import { unifiedFlowPaths } from 'app/navigation/paths'

import Button from 'common/components/button'
import ButtonWithDropdown from 'common/components/ButtonWithDropdown'
import { useAppSelector } from 'common/hooks/redux'
import { isComponentElement } from 'features/unifiedFlow/api/interfaces'
import useApproveElementMutation from 'features/unifiedFlow/api/mutations/useApproveElementMutation'
import {
  getAllElements,
  getIsOptimized,
} from 'features/unifiedFlow/contentPage/helpers'
import {
  useContent,
  useSelectedElement,
} from 'features/unifiedFlow/contentPage/hooks'

type Props = {
  isReadOnly?: boolean
  isApproveButtonDisabled?: boolean
  onSendForApproval?: () => void
}
const ContentTableActionButtons = ({
  isApproveButtonDisabled,
  isReadOnly = false,
  onSendForApproval,
}: Props) => {
  const { content } = useContent()
  const approveElementMutation = useApproveElementMutation({
    redirectTo: unifiedFlowPaths.launch,
  })
  const { data: selectedElement } = useSelectedElement()

  const campaignData = useAppSelector(
    (state) => state.campaignStates.campaignData
  )

  const externalApprovalRequired =
    campaignData?.campaign_configuration?.external_approval_required || false
  const isOptimized = getIsOptimized(selectedElement)

  const allComponentElements =
    getAllElements(content).filter(isComponentElement)

  const approvedElements = allComponentElements.filter(
    (element) => element.approved
  )

  const handleApprove = () => {
    if (selectedElement && content) {
      approveElementMutation.mutate({
        accountId: content.account_id,
        element_id: selectedElement.element_id,
        content_id: content?._id,
      })
    }
  }

  const approveButtonOptions = [
    {
      key: 'send-for-approval',
      label: 'Send for approval',
      onClick: () => {
        onSendForApproval?.()
      },
    },
    {
      key: 'approve',
      label: 'Approve',
      onClick: handleApprove,
    },
  ]

  const isButtonDisabled =
    isApproveButtonDisabled ||
    approveElementMutation.isLoading ||
    selectedElement?.approved ||
    isReadOnly

  return (
    <div className="flex items-center justify-between w-full">
      <p
        className="text-gold-500 font-normal text-sm"
        data-testid="approvals-complete"
      >
        {`${approvedElements.length} out of ${allComponentElements.length} approvals complete`}
      </p>
      {externalApprovalRequired && isOptimized ? (
        <ButtonWithDropdown
          data-testid="approve-button-dropdown"
          variant="primary"
          loading={approveElementMutation.isLoading}
          disabled={isButtonDisabled}
          options={approveButtonOptions}
        >
          Approve
        </ButtonWithDropdown>
      ) : (
        <Button
          data-testid="approve-variant"
          disabled={isButtonDisabled}
          loading={approveElementMutation.isLoading}
          className="ml-4 cta-button"
          variant="primary"
          onClick={handleApprove}
        >
          {selectedElement?.approved ? 'Approved' : 'Approve'}
        </Button>
      )}
    </div>
  )
}

export default ContentTableActionButtons
